/**
 * Adopting the images that arrive with a pasted signature.
 *
 * A signature copied out of Gmail is a small HTML document, and its logo and
 * banner are absolute URLs on Google's servers. Pasting it looks perfect --
 * every image renders, because the browser doing the pasting is signed in to
 * the account they belong to. It is the customer who finds out otherwise:
 * those URLs are per-account or short-lived proxies, so what lands in their
 * inbox is a row of broken images, and nobody at the desk ever sees it.
 *
 * So a paste is followed by adoption: every image the desk does not host is
 * copied into its own library and the tag is rewritten to the copy. The result
 * is a signature whose images are served from the one route that works without
 * a session, which is exactly what a mail client has.
 *
 * Deliberately not silent about failure. An image that cannot be adopted keeps
 * its original URL and is named to the agent, because a signature that quietly
 * lost its logo is worse than one that says so.
 */

import { api } from "./api";
import type { BrandImage } from "../types";

/** Images already served from our own public route need no adoption. */
const PUBLIC_PATH = "/api/public/images/";

export interface AdoptionResult {
  html: string;
  /** How many images were copied into the library. */
  adopted: number;
  /** Reasons, one per image that could not be. */
  failed: string[];
}

/** True for a src the desk already serves, or that it must not touch. */
function isOurs(src: string): boolean {
  if (src.startsWith(PUBLIC_PATH)) return true;
  try {
    const url = new URL(src, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith(PUBLIC_PATH);
  } catch {
    return false;
  }
}

/**
 * A name for the library, from the tag's own alt text or its filename.
 *
 * Worth the effort: these end up in a picker that an admin chooses from later,
 * and "image" four times over is not a picker.
 */
function nameFor(image: HTMLImageElement, src: string): string {
  const alt = image.getAttribute("alt")?.trim();
  if (alt) return alt.slice(0, 96);

  try {
    const last = new URL(src, window.location.origin).pathname.split("/").filter(Boolean).pop();
    if (last && /\.[a-z0-9]{2,4}$/i.test(last)) return decodeURIComponent(last).slice(0, 96);
  } catch {
    // Not a parseable URL; the fallback below is the answer.
  }

  return "Signature image";
}

/** Turn a data: URL into a file the upload endpoint will take. */
async function fileFromDataUrl(src: string, name: string): Promise<File> {
  const blob = await (await fetch(src)).blob();
  const extension = blob.type.split("/")[1]?.replace("+xml", "") ?? "png";
  const filename = /\.[a-z0-9]{2,4}$/i.test(name) ? name : `${name}.${extension}`;
  return new File([blob], filename, { type: blob.type });
}

/**
 * Copy every foreign image in `html` into the library, and point the markup at
 * the copies.
 *
 * Uploads run through the same two endpoints the Images panel uses, so an
 * adopted logo is an ordinary library image afterwards -- listable, insertable
 * into anything else, and removable.
 */
export async function adoptForeignImages(html: string): Promise<AdoptionResult> {
  const document_ = new DOMParser().parseFromString(html, "text/html");
  const images = [...document_.querySelectorAll("img")];

  /* One request per distinct URL: a signature that repeats its logo in a
     banner and a footer should not put two copies in the library. */
  const resolved = new Map<string, string>();
  const failed: string[] = [];
  let adopted = 0;

  for (const image of images) {
    const src = image.getAttribute("src")?.trim() ?? "";
    if (!src || isOurs(src)) continue;

    if (!resolved.has(src)) {
      const name = nameFor(image, src);
      try {
        const stored = await adoptOne(src, name);
        resolved.set(src, `${window.location.origin}${PUBLIC_PATH}${stored.id}`);
        adopted += 1;
      } catch (cause) {
        resolved.set(src, src);
        failed.push(
          `${name}: ${cause instanceof Error ? cause.message : "could not be copied"}`,
        );
      }
    }

    const replacement = resolved.get(src);
    if (replacement && replacement !== src) {
      image.setAttribute("src", replacement);
      /* A srcset would keep pointing at the original, and the mail client is
         free to prefer it -- which is the broken image we just removed. */
      image.removeAttribute("srcset");
    }
  }

  return { html: document_.body.innerHTML, adopted, failed };
}

/** One image, by whichever route its URL allows. */
async function adoptOne(src: string, name: string): Promise<BrandImage> {
  if (src.startsWith("data:")) {
    /* An embedded image: the bytes are already here, so this is an upload
       rather than a fetch. It cannot simply be left in place -- the sanitiser
       refuses data: URLs, because one can carry an SVG with script inside. */
    return api.uploadBrandImage(await fileFromDataUrl(src, name));
  }

  if (src.startsWith("http://") || src.startsWith("https://")) {
    return api.importBrandImage(src, name);
  }

  /*
   * blob: and file: are the two that turn up, from a client that pasted a
   * reference into its own memory or onto its own disk. Neither is readable
   * from here, and neither would have worked for the customer either.
   */
  throw new Error("upload it under Images and insert it with the image button");
}
